import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { defaultLang } from '@/entities/i18next';
import { useAppSelector } from '@hooks/redux/redux';
import { universalNotification } from '@/lib/utils/notification';
import { useUpdateFilialTimezoneMutation } from '@/entities/admin-app/filials/api';

interface FormValues {
  locale: string;
  timezone: string;
}

export const useLocaleTimeZone = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { filial } = useAppSelector((state) => state.filials);
  const { Timezones } = useAppSelector((state) => state.commonSettings.TimezoneSettings);
  const { TimeZone, Culture: locale } = filial?.Settings ?? {};
  const [updateSettings] = useUpdateFilialTimezoneMutation();

  const form = useForm<FormValues>({
    validateInputOnChange: true,
    initialValues: {
      locale: locale || defaultLang,
      timezone: String(TimeZone?.Id ?? 0)
    }
  });

  useEffect(() => {
    if (TimeZone) {
      form.setValues({
        locale: locale || defaultLang,
        timezone: String(TimeZone?.Id ?? 0)
      });
      form.resetDirty();
    }
  }, [TimeZone, locale]);

  const getTimezoneById = (timezoneId: string) => {
    const timezoneItem = Timezones[timezoneId];

    if (!timezoneItem) {
      return null;
    }

    return {
      Id: Number(timezoneId),
      TzIdName: timezoneItem.tzName,
      UtcOffset: timezoneItem.utc,
      UtcOffsetMinutes: timezoneItem.utcOffsetMinutes,
      Name: timezoneItem.label
    };
  };

  const handleSave = async () => {
    if (!filial || !filial.Settings || isLoading) {
      return;
    }
    const { locale, timezone } = form.getValues();
    const timezoneData = getTimezoneById(timezone);

    if (!timezoneData) {
      universalNotification({
        isSuccess: false,
        isError: true,
        error: t('common.savedMessageError'),
        successTitle: ''
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateSettings({
        CustomerId: filial?.Id,
        Type: 9,
        Settings: {
          Culture: locale,
          TimeZone: timezoneData
        }
      });

      universalNotification({
        isSuccess: true,
        isError: false,
        error: '',
        successTitle: t('filials.successSave')
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      universalNotification({
        isSuccess: false,
        isError: true,
        error: t('common.savedMessageError'),
        successTitle: ''
      });
    }
  };

  return {
    form,
    isLoading,
    handleSave
  };
};
